/**
 * @file Contributors related queries.
 */

import gql from 'graphql-tag';

import { AlbumListingDetails, AlbumForReview } from '../fragments/album';
import {
  AlbumFolderBaseDetails,
  AlbumFolderChildrenCount,
  AlbumFolderEventSettingsFields,
} from '../fragments/album-folder';
import { PhotoUrlTypeFields } from '../fragments/custom-prints';
import { AutoContributorSettingsDetails } from '../fragments/contributor-assignments';
import { AlbumPhotoGroupBasicDetails } from '../fragments/album-photo-group';
import { PhotoThumbnailsPhoto } from '../fragments/photo';

export const ContributorAlbumsFromAccount = gql`
  query contributorAlbumsFromAccount(
    $album_name: String
    $offset: Int = 0
    $limit: Int = 20
  ) {
    me {
      id
      contributor_assignment_albums(
        album_name: $album_name
        slice: { offset: $offset, limit: $limit }
      ) {
        total
        records {
          id
          album {
            id
            name
            album_type
            album_subtype
            root_album_folder {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const AlbumForContributors = gql`
  query albumForContributors(
    $id: RecordID!
    $include_user_uploaded_videos: Boolean = false
    $user_uploaded_videos: RecordID
  ) {
    album(id: $id) {
      id
      name
      creator {
        id
      }
      album_photo_groups(slice: { limit: 999 }) {
        total
        records {
          ...AlbumPhotoGroupBasicDetails
        }
      }
      ...AlbumListingDetails
      is_video_album
      purchased_event_addon_types
      greeting_videos: videos(types: [GREETING]) {
        records {
          id
          type
        }
      }
      user_uploaded_videos: videos(
        processed_only: false
        uploader_ids: [$user_uploaded_videos]
      ) @include(if: $include_user_uploaded_videos) {
        total
        records {
          id
          type
        }
      }
    }
  }

  ${AlbumPhotoGroupBasicDetails}
  ${AlbumListingDetails}
`;

export const AlbumFolderForContributors = gql`
  query albumFolderForContributors($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      has_video_album
      joins_enabled
      is_web_gallery
      enable_external_upload
      auto_contributor_settings {
        upload_access
      }
      creator: account {
        id
        name
        organization_name
      }
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
      children {
        albums {
          total
        }
        album_folders {
          total
        }
      }
      root {
        id
        name
      }
      giftable_event_addon_types(exclude_purchased_event_addon_types: true)
    }
  }
  ${AlbumFolderEventSettingsFields}
`;

export const MeForPermissions = gql`
  query MeForPermissions {
    me {
      id
      contributor_hierarchy {
        __typename
        ... on IContributorHierarchyItem {
          id
          parent_id
          item_type
          contributor_assignment {
            id
            upload_access
            view_access
            dnp_access
            album_create_access
          }
        }

        ... on ContributorHierarchyAlbumFolderItem {
          album_folder {
            id
            name
            creator: account {
              id
              name
            }
            album_type
            album_subtype
          }
        }

        ... on ContributorHierarchyAlbumItem {
          album {
            id
            name
            creator {
              id
              name
            }
            album_type
            album_subtype
          }
        }
      }
    }
  }
`;

export const MeWithSharedItems = gql`
  query MeWithSharedItems {
    me {
      id
      contributor_hierarchy {
        __typename
        ... on IContributorHierarchyItem {
          id
          parent_id
          item_type
          contributor_assignment {
            id
            upload_access
            view_access
            dnp_access
          }
        }

        ... on ContributorHierarchyAlbumFolderItem {
          album_folder {
            id
            name
            creator: account {
              id
              name
            }
            album_type
            album_subtype
            cover_photos {
              records {
                id
                faces_box
                thumbnail_urls(ignore_watermark: true) {
                  ...PhotoUrlTypeFields
                }
              }
            }

            ...AlbumFolderBaseDetails
            ...AlbumFolderChildrenCount
          }
        }

        ... on ContributorHierarchyAlbumItem {
          album {
            id
            name
            creator {
              id
              name
            }
            album_type
            album_subtype
            album_photo_groups(slice: { limit: 999 }) {
              total
              records {
                ...AlbumPhotoGroupBasicDetails
              }
            }

            ...AlbumListingDetails
            ...AlbumForReview
          }
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumFolderChildrenCount}
  ${AlbumListingDetails}
  ${AlbumForReview}
  ${AlbumPhotoGroupBasicDetails}
  ${PhotoUrlTypeFields}
`;

export const ContributorInvitationDetails = gql`
  {
    contributorInvitationDetails @client {
      token
      shareId
      email
      phoneNumber
    }
  }
`;

export const ContributorAssignmentShare = gql`
  query contributorAssignmentShare($id: RecordID!) {
    contributorAssignmentShare(id: $id) {
      view_access
      upload_access
      selfie_station_access
      dnp_access
      account {
        id
        email_address
        phone_number
        has_password
      }
      grantor {
        id
        name
        organization_name
        avatar_photo {
          id
          thumbnail_urls {
            url
            size_code
            width
            height
          }
        }
      }
      album {
        id
        name
        is_web_gallery
        album_type
        album_subtype
        photos(slice: { limit: 3 }, for_web_gallery: true) {
          total
          records {
            id
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
      album_folder {
        id
        name
        is_web_gallery
        album_type
        album_subtype
        cover_photos(slice: { limit: 3 }) {
          total
          records {
            id
            faces_box
            thumbnail_urls {
              ...PhotoUrlTypeFields
            }
          }
        }
      }
    }
  }
  ${PhotoUrlTypeFields}
`;

export const AlbumForContributorShare = gql`
  query albumForContributorsShare($id: RecordID!) {
    album(id: $id) {
      id
      name
      album_type
      album_subtype
      enable_external_upload
      auto_contributor_share_url
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      is_web_gallery
      joins_enabled
      is_video_album
      is_wedding_day_album
      is_memory_book
      creator {
        id
        name
        username
        organization_name
        avatar_photo {
          id
          thumbnail_urls {
            url
            size_code
            width
            height
          }
        }
      }
      album_folder {
        id
        name
        album_type
        album_subtype
        account {
          id
          name
          organization_name
        }
        auto_contributor_share_url
        auto_contributor_settings {
          ...AutoContributorSettingsDetails
        }
        individual_albums
        is_web_gallery
        photo_sharing_mode
        joins_enabled
        event_settings {
          ...AlbumFolderEventSettingsFields
        }
      }
      root_album_folder {
        id
        account {
          id
        }
        photo_sharing_mode
      }
      cover_photo {
        faces_box
        ...PhotoThumbnailsPhoto
      }
      slideshow {
        mode
        status
        embed_code
      }
      purchased_event_addon_types
    }
  }
  ${AutoContributorSettingsDetails}
  ${AlbumFolderEventSettingsFields}
  ${PhotoThumbnailsPhoto}
`;

export const AlbumFolderForContributorShare = gql`
  query albumFolderForContributorsShare($id: RecordID!) {
    albumFolder(id: $id) {
      id
      name
      album_type
      album_subtype
      enable_external_upload
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      auto_contributor_share_url
      is_web_gallery
      photo_sharing_mode
      joins_enabled
      cover_photos(slice: { limit: 1 }) {
        records {
          faces_box
          ...PhotoThumbnailsPhoto
        }
      }
      all_photos_mode
      has_album_photos
      web_app_urls {
        all_photos_access_url
      }
      account {
        id
        name
        username
        organization_name
        avatar_photo {
          id
          thumbnail_urls {
            url
            size_code
            width
            height
          }
        }
      }
      children {
        albums(slice: { limit: 999 }) {
          records {
            id
            name
            album_type
            album_subtype
            auto_contributor_share_url
            auto_contributor_settings {
              ...AutoContributorSettingsDetails
            }
          }
        }
      }
      event_settings {
        ...AlbumFolderEventSettingsFields
      }
    }
  }
  ${AutoContributorSettingsDetails}
  ${PhotoThumbnailsPhoto}
  ${AlbumFolderEventSettingsFields}
`;

export const AlbumContributors = gql`
  query albumContributors(
    $id: RecordID!
    $album_photo_filter: AlbumPhotoFilterEnum
  ) {
    album(id: $id) {
      id
      album_type
      album_subtype
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      auto_contributor_share_url
      contributor_assignments(
        include_inherited: true
        album_photo_filter: $album_photo_filter
        slice: { limit: 99 }
      ) {
        records {
          id
          account {
            id
            name
            avatar_photo {
              id
              thumbnail_urls {
                size_code
                url
              }
            }
          }
        }
      }
    }
  }
  ${AutoContributorSettingsDetails}
`;

export const AlbumFolderContributors = gql`
  query albumFolderContributors($id: RecordID!) {
    albumFolder(id: $id) {
      id
      album_type
      album_subtype
      auto_contributor_settings {
        ...AutoContributorSettingsDetails
      }
      auto_contributor_share_url
      contributor_assignments(include_inherited: true, slice: { limit: 99 }) {
        records {
          id
          account {
            id
            name
            avatar_photo {
              id
              thumbnail_urls {
                size_code
                url
              }
            }
          }
        }
      }
    }
  }
  ${AutoContributorSettingsDetails}
`;

export const MeWithJoinedContent = gql`
  query MeWithJoinedContent {
    me {
      id
      hierarchy_groups(slice: { limit: 999 }, owner_status: NOT_OWNER) {
        records {
          id
          album_membership_group {
            id
            display_name
            is_album
            cover_photos(slice: { limit: 1 }) {
              records {
                id
                faces_box
                ...PhotoThumbnailsPhoto
              }
            }
            matched_photos_extended {
              total
            }
          }
          contributor_hierarchy_items {
            records {
              __typename
              ... on IContributorHierarchyItem {
                id
                parent_id
                item_type
                contributor_assignment {
                  id
                  upload_access
                  view_access
                  dnp_access
                }
              }

              ... on ContributorHierarchyAlbumFolderItem {
                album_folder {
                  id
                  name
                  creator: account {
                    id
                    name
                  }
                  album_type
                  album_subtype
                  cover_photos {
                    records {
                      id
                      faces_box
                      thumbnail_urls(ignore_watermark: true) {
                        ...PhotoUrlTypeFields
                      }
                    }
                  }

                  ...AlbumFolderBaseDetails
                  ...AlbumFolderChildrenCount
                }
              }

              ... on ContributorHierarchyAlbumItem {
                album {
                  id
                  name
                  creator {
                    id
                    name
                  }
                  album_type
                  album_subtype
                  album_photo_groups(slice: { limit: 999 }) {
                    total
                    records {
                      ...AlbumPhotoGroupBasicDetails
                    }
                  }

                  ...AlbumListingDetails
                  ...AlbumForReview
                }
              }
            }
          }
        }
      }
    }
  }
  ${AlbumFolderBaseDetails}
  ${AlbumFolderChildrenCount}
  ${AlbumListingDetails}
  ${AlbumForReview}
  ${AlbumPhotoGroupBasicDetails}
  ${PhotoUrlTypeFields}
  ${PhotoThumbnailsPhoto}
`;
